import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
    X, Star, FileText, Download, AlertCircle, Loader2
} from "lucide-react";
import { OfferStatusEnum } from "../../modules/shared/model/offer.interfaces";
import { calculateEmergencyCost } from "../../lib/emergencyCostUtils";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { updateOffer } from "../../redux/Slices/offerSlice";
import { createConversation, createChat } from "../../redux/Actions/AuthAction";
import { fetchParticipantChats, updateChat } from "../../redux/Actions/ChatActions";
import { ConversationTypeEnum } from "../../modules/shared/model/conversationType.enum";
import { ContextEnum } from "../../modules/shared/model/context.enum";
import { useWebSocket } from "../../hooks/useWebSocket";
import { checkUserBillingInfo } from "../../utils/billingUtils";
import BillingAddressRequirementDialog from "./BillingAddressRequirementDialog";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";

interface OfferDetailsModalProps {
    offer: any;
    onClose: (messageData?: any) => void;
    activeTab?: string;
    onRefresh?: (messageData?: any) => void; // Callback to refresh data after operations
    categoryFormSettings?: any;
    // Optional: If not provided, the modal will handle operations internally
    onUpdateOffer?: (offer: any, status: number) => void;
    onDeleteOffer?: (offer: any) => void;
    onPermanentlyDeleteOffer?: (offer: any) => void;
    // Hide delete buttons when opened from MessagesAndVideoCall
    hideDeleteButtons?: boolean;
}

const OfferDetailsModal: React.FC<OfferDetailsModalProps> = ({
    offer,
    onClose,
    activeTab = "received",
    onRefresh,
    categoryFormSettings,
    onUpdateOffer,
    onDeleteOffer,
    onPermanentlyDeleteOffer,
    hideDeleteButtons = false
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const { sendMessage: sendWebSocketMessage, isConnected } = useWebSocket();
    const [messageData, setMessageData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingAction, setLoadingAction] = useState<string | null>(null);
    const [showBillingAddressDialog, setShowBillingAddressDialog] = useState(false);
    const [selectedOfferForBilling, setSelectedOfferForBilling] = useState<any>(null);

    if (!offer) return null;

    // Function to create chat and send conversation message
    const createChatAndSendMessage = async (conversationType: ConversationTypeEnum, message: string) => {
        try {
            // Get client and professional IDs from offer
            const clientId = offer?.job?.userId?.id;
            const professionalId = offer?.user?.id;

            if (!clientId || !professionalId) {
                console.warn("Missing client or professional ID in offer, skipping conversation message");
                return;
            }

            // Check if chat already exists between client and professional
            let existingChat = null;
            try {
                existingChat = await dispatch(fetchParticipantChats({ currentUserId: clientId, page: 1, limit: 50 })).unwrap();
            } catch (error) {
                // No existing chat found, will create new one
            }

            let chatResponse;
            if (existingChat.chats && existingChat.chats.length > 0) {
                const filteredChats = existingChat.chats.filter((chat: any) => {
                    const participantIds = chat.chat.participants.map((p: any) => p.id);
                    return participantIds.includes(clientId) && participantIds.includes(professionalId);
                });
                const chat = filteredChats[0].chat;
                chatResponse = chat;

                // Update the chat with lastMessage and lastMessageAt
                if (chatResponse && chatResponse.id) {
                    const updatedChat = {
                        lastMessage: message,
                        lastMessageAt: new Date().toISOString(),
                        unreadCount: { [clientId]: user?.id === professionalId ? 1 : 0, [professionalId]: user?.id === clientId ? 1 : 0 },
                    };
                    await dispatch(updateChat({
                        chatId: chatResponse.id,
                        data: updatedChat
                    }));
                }
            } else {
                // Create new chat
                const chatPayload = {
                    unreadCount: { [clientId]: user?.id === professionalId ? 0 : 1, [professionalId]: user?.id === clientId ? 0 : 1 },
                    participants: [
                        { id: clientId },
                        { id: professionalId }
                    ],
                    contextId: offer?.job?.id,
                    contextType: { id: ContextEnum.Message },
                    lastMessageAt: new Date().toISOString(),
                    lastMessage: message,
                };

                chatResponse = await dispatch(createChat({ data: chatPayload })).unwrap();
            }

            // Send conversation message
            const conversationPayload = {
                isRead: false,
                message: message,
                sender: { id: user?.id },
                chatId: { id: chatResponse.id },
                type: { id: conversationType },
                referenceId: offer?.id,
                attachments: []
            };

            const conversation = await dispatch(createConversation({ data: conversationPayload })).unwrap();
            console.log("conversation", conversation);

            // Send WebSocket message to notify other participants (non-blocking)
            setTimeout(async () => {
                try {
                    if (sendWebSocketMessage && isConnected) {
                        const receivers = chatResponse.participants.map((p: any) => p.id).filter((id: any) => id !== user?.id);
                        if (receivers.length > 0) {
                            const messageData = {
                                receivers,
                                sender: user?.id || "",
                                type: "new_conversation",
                                detail: {
                                    conversationId: conversation.id,
                                    conversation: conversation,
                                    chatId: conversation.chatId.id,
                                    message: conversationPayload.message,
                                    senderId: conversation.sender.id,
                                    attachments: conversationPayload.attachments,
                                    type: conversationPayload.type.id,
                                    referenceId: conversationPayload.referenceId,
                                    timestamp: new Date().toISOString()
                                },
                                conversationId: conversationPayload.chatId.id
                            };
                            setMessageData(messageData);
                            await sendWebSocketMessage(messageData);

                        }
                    }
                } catch (wsError) {
                    // Don't fail the entire operation if WebSocket fails
                }
            }, 100);
        } catch (error) {
            console.error("Error creating chat or sending message:", error);
            // Don't throw error here as the main action should still proceed
        }
    };

    useEffect(() => {
        if (messageData) {
            onRefresh(messageData);
        }
    }, [messageData]);

    // Handle billing saved callback
    const handleBillingSaved = () => {
        // Check if user now has billing information
        const billingCheck = checkUserBillingInfo(user);
        
        if (billingCheck.hasBillingInfo && selectedOfferForBilling) {
            // Proceed with accepting the offer
            handleUpdateOfferFinal(selectedOfferForBilling, OfferStatusEnum.Accepted);
            setSelectedOfferForBilling(null);
        }
    };

    // Internal handler functions (used when external handlers are not provided)
    const handleUpdateOfferInternal = async (offer: any, status: number) => {
        // Check if user has billing information for accepting offers
        const billingCheck = checkUserBillingInfo(user);
        
        if (!billingCheck.hasBillingInfo && status === OfferStatusEnum.Accepted) {
            // Store the offer for auto-accepting after billing setup
            setSelectedOfferForBilling(offer);
            // Show billing address requirement dialog
            setShowBillingAddressDialog(true);
            return;
        }

        const actionName = status === OfferStatusEnum.Accepted ? "accepting" : "declining";
        setIsLoading(true);
        setLoadingAction(actionName);

        try {
            await dispatch(updateOffer({
                id: offer.id,
                data: {
                    status: {
                        id: status
                    },
                    timelineType: { id: Number(offer.timelineType.id) },
                    files: offer.files ? offer.files : [],
                    user: { id: offer.user.id },
                    job: { id: offer?.job?.id },
                    amount: offer.amount || 0,
                    title: offer.title || offer.projectTitle || "",
                    description: offer.description || "",
                    isWarranty: offer.isWarranty || false,
                    isPartsIncluded: offer.isPartsIncluded || false,
                    timeline: offer.timeline || 1,
                }
            }));

            // Send conversation message based on status
            if (status === OfferStatusEnum.Accepted) {
                await createChatAndSendMessage(
                    ConversationTypeEnum.Quote_Accepted,
                    t("offerDetailsModal.messages.quoteAccepted")
                );
            } else if (status === OfferStatusEnum.Rejected) {
                await createChatAndSendMessage(
                    ConversationTypeEnum.Quote_Rejected,
                    t("offerDetailsModal.messages.quoteRejected")
                );
            }
            setTimeout(() => {
                onClose(messageData);
                // Refresh data if callback is provided
                if (onRefresh) {
                    onRefresh();
                }
            }, 500);

        } catch (error) {
            console.error("Failed to update offer:", error);
            throw error;
        } finally {
            setIsLoading(false);
            setLoadingAction(null);
        }
    };

    const handleDeleteOfferInternal = async (offer: any) => {
        const isConfirmed = window.confirm(
            t("offerDetailsModal.confirmations.deleteOffer")
        );

        if (!isConfirmed) return;

        setIsLoading(true);
        setLoadingAction("deleting");

        try {
            await dispatch(updateOffer({
                id: offer.id,
                data: {
                    status: {
                        id: 8 // Deleted status
                    },
                    timelineType: { id: Number(offer.timelineType.id) },
                    files: offer.files ? offer.files : [],
                    user: { id: offer.user.id },
                    job: { id: offer?.job?.id },
                    amount: offer.amount || 0,
                    title: offer.title || offer.projectTitle || "",
                    description: offer.description || "",
                    isWarranty: offer.isWarranty || false,
                    isPartsIncluded: offer.isPartsIncluded || false,
                    timeline: offer.timeline || 1,
                }
            }));

            onClose();
            // Refresh data if callback is provided
            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error("Failed to delete offer:", error);
            throw error;
        } finally {
            setIsLoading(false);
            setLoadingAction(null);
        }
    };

    const handlePermanentlyDeleteOfferInternal = async (offer: any) => {
        const isConfirmed = window.confirm(
            t("offerDetailsModal.confirmations.permanentlyDeleteOffer")
        );

        if (!isConfirmed) return;

        setIsLoading(true);
        setLoadingAction("permanently deleting");

        try {
            await dispatch(updateOffer({
                id: offer.id,
                data: {
                    status: {
                        id: 10 // Permanently deleted status
                    },
                    timelineType: { id: Number(offer.timelineType.id) },
                    files: offer.files ? offer.files : [],
                    user: { id: offer.user.id },
                    job: { id: offer?.job?.id },
                    amount: offer.amount || 0,
                    title: offer.title || offer.projectTitle || "",
                    description: offer.description || "",
                    isWarranty: offer.isWarranty || false,
                    isPartsIncluded: offer.isPartsIncluded || false,
                    timeline: offer.timeline || 1,
                }
            }));

            onClose();
            // Refresh data if callback is provided
            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error("Failed to permanently delete offer:", error);
            throw error;
        } finally {
            setIsLoading(false);
            setLoadingAction(null);
        }
    };

    // Use external handlers if provided, otherwise use internal handlers
    const handleUpdateOfferFinal = onUpdateOffer || handleUpdateOfferInternal;
    const handleDeleteOfferFinal = onDeleteOffer || handleDeleteOfferInternal;
    const handlePermanentlyDeleteOfferFinal = onPermanentlyDeleteOffer || handlePermanentlyDeleteOfferInternal;

    // Safety check for required properties
    if (!offer.id) {
        return null;
    }

    const isPurchasedQuote = offer.type === "purchased";

    // Helper function to check if file is an image
    const isImage = (file: any) => {
        const imageTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
        return imageTypes.includes(file.type?.toLowerCase()) ||
            file.name?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|svg)$/);
    };

    // Helper function to check if file is a video
    const isVideo = (file: any) => {
        const videoTypes = ["video/mp4", "video/avi", "video/mov", "video/wmv", "video/flv", "video/webm"];
        return videoTypes.includes(file.type?.toLowerCase()) ||
            file.name?.toLowerCase().match(/\.(mp4|avi|mov|wmv|flv|webm)$/);
    };

    // Helper function to get file extension
    const getFileExtension = (filename: string) => {
        return filename?.split(".").pop()?.toLowerCase() || "";
    };

    // Helper function to download file
    const handleDownload = async (file: any) => {
        try {
            if (file.url || file.path) {
                const response = await fetch(file.url || file.path);
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = file.name || `file-${file.id}.${getFileExtension(file.name)}`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                // Fallback for files without direct URL
                const a = document.createElement("a");
                a.href = `data:${file.type || "application/octet-stream"};base64,${file.content || ""}`;
                a.download = file.name || `file-${file.id}.${getFileExtension(file.name)}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
        } catch (error) {
            console.error("Download failed:", error);
            alert(t("offerDetailsModal.messages.downloadFailed"));
        }
    };

    // Loading spinner component
    const LoadingSpinner = () => (
        <Loader2 className="h-4 w-4 animate-spin" />
    );

    // Status badge component
    const StatusBadge = ({ status }: { status: any }) => {
        // Handle both string and object status formats
        const getStatusInfo = (status: any) => {
            if (typeof status === "object" && status !== null) {
                const statusId = status.id;
                const statusMap: { [key: number]: { text: string; style: string } } = {
                    1: { text: t("offerDetailsModal.statusBadges.draft"), style: "bg-gray-100 text-gray-800" },
                    2: { text: t("offerDetailsModal.statusBadges.approved"), style: "bg-yellow-100 text-yellow-800" },
                    3: { text: t("offerDetailsModal.statusBadges.accepted"), style: "bg-green-100 text-green-800" },
                    4: { text: t("offerDetailsModal.statusBadges.rejected"), style: "bg-red-100 text-red-800" },
                    5: { text: t("offerDetailsModal.statusBadges.expired"), style: "bg-gray-100 text-gray-800" },
                    6: { text: t("offerDetailsModal.statusBadges.suspended"), style: "bg-orange-100 text-orange-800" },
                    7: { text: t("offerDetailsModal.statusBadges.completed"), style: "bg-blue-100 text-blue-800" },
                    8: { text: t("offerDetailsModal.statusBadges.archived"), style: "bg-purple-100 text-purple-800" },
                    9: { text: t("offerDetailsModal.statusBadges.deletedCustomer"), style: "bg-red-100 text-red-800" },
                    10: { text: t("offerDetailsModal.statusBadges.permanentlyDeleted"), style: "bg-red-900 text-white" }
                };
                return statusMap[statusId] || { text: t("offerDetailsModal.statusBadges.unknown"), style: "bg-gray-100 text-gray-800" };
            } else {
                // Handle string status (for purchased quotes)
                const statusStyles: { [key: string]: string } = {
                    active: "bg-purple-100 text-purple-800",
                    responded: "bg-teal-100 text-teal-800"
                };
                const statusText: { [key: string]: string } = {
                    active: t("offerDetailsModal.statusBadges.active"),
                    responded: t("offerDetailsModal.statusBadges.responded")
                };
                return {
                    text: statusText[status] || status,
                    style: statusStyles[status] || "bg-gray-100 text-gray-800"
                };
            }
        };

        const statusInfo = getStatusInfo(status);

        return (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.style}`}>
                {statusInfo.text}
            </span>
        );
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        >
            <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 relative"
            >
                {/* Loading Overlay */}
                {isLoading && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-xl">
                        <div className="flex flex-col items-center gap-3">
                            <LoadingSpinner />
                            <p className="text-sm text-gray-600 capitalize">
                                {t("offerDetailsModal.loading", { action: loadingAction })}
                            </p>
                        </div>
                    </div>
                )}
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">{offer.projectTitle}</h2>
                        <p className="text-sm text-gray-600">{t("offerDetailsModal.offerId")}: {offer.id}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                        <X className="h-6 w-6 text-gray-500" />
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-2">{isPurchasedQuote ? t("offerDetailsModal.quoteDetails") : t("offerDetailsModal.offerDetails")}</h3>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("offerDetailsModal.status")}</span>
                                <StatusBadge status={offer.status} />
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{isPurchasedQuote ? t("offerDetailsModal.purchaseAmount") : t("offerDetailsModal.amount")}</span>
                                <span className="font-medium">
                                    {isPurchasedQuote
                                        ? `${offer.purchaseCurrency === "USD" ? getDynamicCurrencySymbol() : "💎"}${(offer.purchaseAmount || 0).toFixed(2)}`
                                        : `${getDynamicCurrencySymbol()}${(offer.amount || offer.job?.budget || 0).toFixed(2)}`}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{isPurchasedQuote ? t("offerDetailsModal.purchaseDate") : t("offerDetailsModal.createdDate")}</span>
                                <span>
                                    {isPurchasedQuote
                                        ? new Date(offer.purchaseDate || Date.now()).toLocaleDateString()
                                        : new Date(offer.createdAt || Date.now()).toLocaleDateString()
                                    }
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("offerDetailsModal.expectedDuration")}</span>
                                <span>{offer.timeline} {offer.timelineType?.name}</span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-2">{t("offerDetailsModal.clientInformation")}</h3>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("offerDetailsModal.clientName")}</span>
                                <span>{(offer.job?.userId?.firstName && offer.job?.userId?.lastName)
                                    ? `${offer.job.userId.firstName} ${offer.job.userId.lastName}`
                                    : (offer.job?.userId?.firstName || offer.job?.userId?.name || t("offerDetailsModal.notSpecified"))}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("offerDetailsModal.rating")}</span>
                                <div className="flex items-center gap-1">
                                    <Star className="h-4 w-4 text-yellow-400 fill-current" />
                                    <span>{offer.job?.userId?.averageRating || 0}</span>
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("offerDetailsModal.totalJobs")}</span>
                                <span>{offer.client?.totalJobs || offer.job?.userId?.totalJobs || t("offerDetailsModal.notSpecified")} {t("offerDetailsModal.jobs")}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">{t("offerDetailsModal.location")}</span>
                                <span>{offer.client?.location || offer.job?.userId?.location || offer.job?.userId?.address || t("offerDetailsModal.notSpecified")}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">{t("offerDetailsModal.projectDescription")}</h3>
                    <p className="text-gray-600">{offer.description || offer.job?.description || t("offerDetailsModal.noDescriptionAvailable")}</p>
                </div>

                {/* Emergency Exit Cost */}
                {(offer.job?.isEmergency || offer.isEmergency) && (
                    <div className="mb-6">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <AlertCircle className="h-5 w-5 text-red-600" />
                                <h3 className="text-sm font-medium text-red-800">{t("offerDetailsModal.emergencyService")}</h3>
                            </div>
                            <p className="text-sm text-red-700">
                                {t("offerDetailsModal.emergencyServiceFee", {
                                    amount: calculateEmergencyCost(
                                        offer.job?.emergencyTimeSlot || offer.emergencyTimeSlot,
                                        offer.job?.emergencyDays || offer.emergencyDays,
                                        categoryFormSettings
                                    )
                                })}
                            </p>
                        </div>
                    </div>
                )}

                <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">{t("offerDetailsModal.requirements")}</h3>
                    {offer.requirements && offer.requirements.length > 0 ? (
                        <ul className="list-disc pl-5 space-y-1">
                            {offer.requirements.map((req: string, index: number) => (
                                <li key={index} className="text-gray-600">{req}</li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-gray-500 italic">{t("offerDetailsModal.noSpecificRequirements")}</p>
                    )}
                </div>

                {/* Media Files Section */}
                {(offer.files && offer.files.length > 0) && (
                    <div className="mb-6">
                        <h3 className="text-sm font-medium text-gray-700 mb-4">{t("offerDetailsModal.attachmentsMediaFiles")}</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {offer.files.map((file: any, index: number) => (
                                <div key={index} className="border rounded-lg overflow-hidden bg-gray-50">
                                    {isImage(file) ? (
                                        <div className="relative group">
                                            <img
                                                src={file.url || file.path || `data:${file.type};base64,${file.content}`}
                                                alt={file.name || `Image ${index + 1}`}
                                                className="w-full h-32 object-cover"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02MCAxMDAgTDEwMCA2MCBMMTQwIDEwMEg2MFoiIGZpbGw9IiM5Q0EzQUYiLz4KPHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIwIDIwQzIwIDE3LjggMTcuOCAxNSAxNSAxNUMxMi4yIDE1IDEwIDE3LjIgMTAgMjBDMTAgMjIuOCAxMi4yIDI1IDE1IDI1QzE3LjggMjUgMjAgMjIuOCAyMCAyMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+Cjwvc3ZnPgo=";
                                                }}
                                            />
                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                                                <button
                                                    onClick={() => handleDownload(file)}
                                                    className="opacity-0 group-hover:opacity-100 bg-white text-gray-800 px-3 py-2 rounded-lg shadow-lg hover:bg-gray-100 transition-all duration-200 flex items-center gap-2"
                                                >
                                                    <Download className="h-4 w-4" />
                                                    {t("offerDetailsModal.download")}
                                                </button>
                                            </div>
                                        </div>
                                    ) : isVideo(file) ? (
                                        <div className="relative group">
                                            <video
                                                src={file.url || file.path || `data:${file.type};base64,${file.content}`}
                                                className="w-full h-32 object-cover"
                                                controls
                                                preload="metadata"
                                            />
                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                                                <button
                                                    onClick={() => handleDownload(file)}
                                                    className="opacity-0 group-hover:opacity-100 bg-white text-gray-800 px-3 py-2 rounded-lg shadow-lg hover:bg-gray-100 transition-all duration-200 flex items-center gap-2"
                                                >
                                                    <Download className="h-4 w-4" />
                                                    {t("offerDetailsModal.download")}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="p-4 text-center">
                                            <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                            <p className="text-sm text-gray-600 truncate">{file.name || `File ${index + 1}`}</p>
                                            <p className="text-xs text-gray-500">{getFileExtension(file.name)}</p>
                                        </div>
                                    )}
                                    <div className="p-3 border-t">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-700 truncate">
                                                {file.name || `File ${index + 1}`}
                                            </span>
                                            <button
                                                onClick={() => handleDownload(file)}
                                                className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-50 rounded"
                                                title={t("offerDetailsModal.downloadFile")}
                                            >
                                                <Download className="h-4 w-4" />
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {file.type || t("offerDetailsModal.unknownType")} • {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : t("offerDetailsModal.fileSize")}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                {Number(offer.status?.id) !== OfferStatusEnum.Deleted && Number(offer.status?.id) !== OfferStatusEnum.Admin_Suspended && Number(offer.status?.id) !== OfferStatusEnum.Admin_Deleted &&
                    <div className="flex justify-end gap-3">
                        {activeTab === "sent" ? (
                            <button
                                onClick={onClose}
                                disabled={isLoading}
                                className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {t("offerDetailsModal.close")}
                            </button>
                        ) : !isPurchasedQuote ? (
                            <>
                                {(Number(offer.status?.id) !== OfferStatusEnum.Rejected && offer.isSent) &&
                                    <button
                                        onClick={() => handleUpdateOfferFinal(offer, OfferStatusEnum.Rejected)}
                                        disabled={isLoading}
                                        className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        {loadingAction === "declining" && <LoadingSpinner />}
                                        {loadingAction === "declining" ? t("offerDetailsModal.declining") : t("offerDetailsModal.declineOffer")}
                                    </button>}
                                {(Number(offer.status?.id) !== OfferStatusEnum.Accepted && offer.isSent) &&
                                    <button
                                        onClick={() => handleUpdateOfferFinal(offer, OfferStatusEnum.Accepted)}
                                        disabled={isLoading}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        {loadingAction === "accepting" && <LoadingSpinner />}
                                        {loadingAction === "accepting" ? t("offerDetailsModal.accepting") : t("offerDetailsModal.acceptOffer")}
                                    </button>}
                                {!hideDeleteButtons && (
                                    <button
                                        onClick={() => handleDeleteOfferFinal(offer)}
                                        disabled={isLoading}
                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        {loadingAction === "deleting" && <LoadingSpinner />}
                                        {loadingAction === "deleting" ? t("offerDetailsModal.deleting") : t("offerDetailsModal.deleteOffer")}
                                    </button>
                                )}
                            </>
                        ) : !isPurchasedQuote && offer.status?.name === "Accepted" ? (
                            <button
                                disabled={isLoading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {t("offerDetailsModal.viewProject")}
                            </button>
                        ) : isPurchasedQuote ? (
                            <button
                                disabled={isLoading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {t("offerDetailsModal.viewQuoteDetails")}
                            </button>
                        ) : null}
                    </div>}

                {(Number(offer.status?.id) === OfferStatusEnum.Deleted || Number(offer.status?.id) === OfferStatusEnum.Archived || Number(offer.status?.id) === OfferStatusEnum.Admin_Deleted) && !hideDeleteButtons && (
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handlePermanentlyDeleteOfferFinal(offer);
                            }}
                            disabled={isLoading}
                            className="px-4 py-2 bg-red-900 text-white rounded-lg hover:bg-red-950 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {loadingAction === "permanently deleting" && <LoadingSpinner />}
                            {loadingAction === "permanently deleting" ? t("offerDetailsModal.permanentlyDeleting") : t("offerDetailsModal.permanentlyDelete")}
                        </button>
                    </div>
                )}
            </motion.div>

            {/* Billing Address Requirement Dialog */}
            <BillingAddressRequirementDialog
                isOpen={showBillingAddressDialog}
                onClose={() => {
                    setShowBillingAddressDialog(false);
                    setSelectedOfferForBilling(null);
                }}
                onBillingSaved={handleBillingSaved}
                userRole={Number(user?.role?.id)}
                action="payment"
            />
        </motion.div>
    );
};

export default OfferDetailsModal;
